/**
 * 4/20 Special — Free Grinder Promotion Widget
 *
 * - Single box: pick 1 free grinder
 * - BOGO: pick 2 free grinders
 * - Color-matching: selected box color moves matching grinder to front
 * - Order summary with savings breakdown
 * - ATC button text: "$X + N FREE Grinders"
 * - Cart injection: adds free grinder items with _420_free_grinder property
 */
(function () {
  'use strict';

  /* ---------- Color mapping: StashBox color → Grinder color ---------- */
  /* Single config object — update here when new colorways are added.   */
  /* Available grinder colors: Black, Pink, Purple, Red, Green, Teal, Blue, Yellow */
  var COLOR_MAP = {
    'bamboo': 'black',
    'black': 'black',
    'pink': 'pink',
    'purple': 'purple',
    'red': 'red',
    'green': 'green',
    'teal': 'teal',
    'royal blue': 'blue',
    'blue': 'blue',
    'yellow': 'yellow',
    /* Neon / Limited Edition — no matching grinder, fall back to Black */
    'neon pink': 'black',
    'neon orange': 'black',
    'neon green': 'black'
  };

  /* ========================================================
     Promo420 Controller
     ======================================================== */
  function Promo420(el) {
    this.el = el;
    this.pickers = Array.from(el.querySelectorAll('[data-promo-picker]'));
    this.summaryEl = el.querySelector('[data-promo-summary]');
    this.summaryLines = el.querySelector('[data-promo-summary-lines]');
    this.savingsRow = el.querySelector('[data-promo-savings]');
    this.savingsValue = el.querySelector('[data-promo-savings-value]');
    this.totalValue = el.querySelector('[data-promo-total-value]');
    // Header subtitle inside the widget
    this.headerSubtitleText = el.querySelector('[data-promo-header-subtitle-text]');
    this.freeCountEl = el.querySelector('[data-promo-free-count]');
    this.giftValueEl = el.querySelector('[data-promo-gift-value]');
    // Pill in the variant picker area
    this.subtitleText = document.querySelector('[data-promo-subtitle-text]');
    this.subtitleTextOriginal = this.subtitleText ? this.subtitleText.textContent : '';

    // Config from data attributes
    this.grinderVariants = jsonParse(el.dataset.grinderVariants) || [];
    this.grinderPrice = parseInt(el.dataset.grinderPrice, 10) || 0;
    this.singleFreeCount = parseInt(el.dataset.singleFreeCount, 10) || 1;
    this.bogoFreeCount = parseInt(el.dataset.bogoFreeCount, 10) || 2;
    this.bogoDiscountLabel = el.dataset.bogoDiscountLabel || '42% off Box 2';
    this.bogoDiscountPercent = parseInt(el.dataset.bogoDiscountPercent, 10) || 42;
    this.productName = el.dataset.productName || 'OG Stash Box';

    // State
    this.isBogo = false;
    this.selectedGrinders = []; // array of { variantId, color, price } per picker
    this.boxColors = { box1: null, box2: null };

    this.initDefaults();
    this.bindCardEvents();
    this.initDragScroll();
    this.listenForVariantChanges();
    this.listenForTabChanges();
    this.interceptFormSubmission();
    this.detectBogoState();
    this.syncInitialBoxColors();
    this.updateUI();
  }

  function jsonParse(str) {
    try { return JSON.parse(str || '[]'); } catch (e) { return []; }
  }

  /**
   * Resolve formId from variant:change event detail.
   * Due to DOM clobbering (<input name="id"> inside a <form>),
   * form.id returns the input element instead of the id string.
   */
  function resolveFormId(raw) {
    if (typeof raw === 'string') return raw;
    if (raw && raw.form) return raw.form.getAttribute('id') || '';
    return '';
  }

  function capitalize(str) {
    if (!str) return '';
    return str.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function formatMoney(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  /* ---------- Read initial box color from variant picker and sync grinders ---------- */
  Promo420.prototype.syncInitialBoxColors = function () {
    // Read selected variant JSON from variant-picker elements
    var pickers = document.querySelectorAll('variant-picker');
    pickers.forEach(function (vp) {
      var formId = vp.getAttribute('form-id') || '';
      var script = vp.querySelector('script[data-variant]');
      if (!script) return;
      try {
        var variant = JSON.parse(script.textContent);
        var color = (variant.option1 || '').toLowerCase().trim();
        if (!color) return;

        if (formId === 'b1g1') {
          this.boxColors.box1 = color;
          this.moveMatchingGrinderToFront(0, color);
        } else if (formId === 'b1g1_top') {
          this.boxColors.box2 = color;
          this.moveMatchingGrinderToFront(1, color);
        } else if (!this.boxColors.box1) {
          // Single box mode — use the main product form's variant picker
          this.boxColors.box1 = color;
          this.moveMatchingGrinderToFront(0, color);
        }
      } catch (e) { /* ignore parse errors */ }
    }.bind(this));
  };

  /* ---------- Init: pre-select first available variant in each picker ---------- */
  Promo420.prototype.initDefaults = function () {
    var self = this;
    this.pickers.forEach(function (picker, idx) {
      var firstSelected = picker.querySelector('.promo-420__card--selected');
      if (firstSelected) {
        self.selectedGrinders[idx] = {
          variantId: firstSelected.dataset.variantId,
          color: firstSelected.dataset.optionName,
          price: parseInt(firstSelected.dataset.price, 10) || 0
        };
      }
    });
  };

  /* ---------- Card click → select grinder ---------- */
  Promo420.prototype.bindCardEvents = function () {
    var self = this;

    this.pickers.forEach(function (picker, pickerIdx) {
      var cards = picker.querySelectorAll('[data-promo-card]');
      cards.forEach(function (card) {
        card.addEventListener('click', function (e) {
          e.preventDefault();
          // Deselect all in this picker
          picker.querySelectorAll('[data-promo-card]').forEach(function (c) {
            c.classList.remove('promo-420__card--selected');
          });
          card.classList.add('promo-420__card--selected');

          // Update state
          self.selectedGrinders[pickerIdx] = {
            variantId: card.dataset.variantId,
            color: card.dataset.optionName,
            price: parseInt(card.dataset.price, 10) || 0
          };

          // Update label
          var label = picker.querySelector('[data-picker-selected-name]');
          if (label) label.textContent = card.dataset.optionName;

          self.updateUI();
        });
      });
    });
  };

  /* ---------- Desktop drag-scroll for swatch cards ---------- */
  Promo420.prototype.initDragScroll = function () {
    var containers = this.el.querySelectorAll('.promo-420__picker-cards');
    containers.forEach(function (container) {
      var isDown = false;
      var startX = 0;
      var scrollLeft = 0;
      var hasDragged = false;
      var wrap = container.parentElement;

      // Check if scrolled to end and update chevron
      function checkScrollEnd() {
        if (!wrap) return;
        var atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
        if (atEnd) {
          wrap.classList.add('scrolled-end');
        } else {
          wrap.classList.remove('scrolled-end');
        }
      }

      container.addEventListener('scroll', checkScrollEnd, { passive: true });
      checkScrollEnd();

      // Arrow click → scroll right by ~2 cards
      var arrow = wrap ? wrap.querySelector('[data-scroll-arrow]') : null;
      if (arrow) {
        arrow.addEventListener('click', function () {
          container.scrollBy({ left: 260, behavior: 'smooth' });
        });
      }

      container.addEventListener('mousedown', function (e) {
        isDown = true;
        hasDragged = false;
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        container.classList.add('is-dragging');
      });

      container.addEventListener('mouseleave', function () {
        isDown = false;
        container.classList.remove('is-dragging');
      });

      container.addEventListener('mouseup', function () {
        isDown = false;
        container.classList.remove('is-dragging');
      });

      container.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - container.offsetLeft;
        var walk = (x - startX) * 1.5;
        if (Math.abs(walk) > 3) hasDragged = true;
        container.scrollLeft = scrollLeft - walk;
      });

      // Prevent click on cards if user was dragging
      container.addEventListener('click', function (e) {
        if (hasDragged) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
    });
  };

  /* ---------- Color matching: listen for StashBox variant changes ---------- */
  Promo420.prototype.listenForVariantChanges = function () {
    var self = this;

    document.addEventListener('variant:change', function (e) {
      var detail = e.detail || {};
      var variant = detail.variant;
      if (!variant) return;

      // detail.formId may be an <input name="id"> element (DOM clobbering)
      // rather than a string — resolve to the actual form id attribute
      var formId = resolveFormId(detail.formId);

      var color = (variant.option1 || '').toLowerCase().trim();
      if (!color) return;

      self.detectBogoState();

      if (self.isBogo) {
        if (formId === 'b1g1') {
          self.boxColors.box1 = color;
          self.moveMatchingGrinderToFront(0, color);
        } else if (formId === 'b1g1_top') {
          self.boxColors.box2 = color;
          self.moveMatchingGrinderToFront(1, color);
        }
      } else {
        self.boxColors.box1 = color;
        self.boxColors.box2 = null;
        self.moveMatchingGrinderToFront(0, color);
      }

      // Update data-final-price on the single-box button so summary reads the correct price
      if (variant.price && !self.isBogo) {
        document.querySelectorAll('.hash-btn button').forEach(function (btn) {
          if (btn.dataset.finalPrice && !btn.dataset.bogoFinalPrice) {
            btn.dataset.finalPrice = formatMoney(variant.price);
          }
        });
      }

      self.updateUI();

      // Gentle scroll so the widget is visible
      self.scrollWidgetIntoView();
    });
  };

  /* ---------- Scroll page so widget header is just visible ---------- */
  Promo420.prototype.scrollWidgetIntoView = function () {
    var rect = this.el.getBoundingClientRect();
    // Only scroll if the widget header is below the viewport
    if (rect.top > window.innerHeight - 100) {
      // Scroll so the widget sits ~120px from the bottom of the viewport
      var targetY = window.pageYOffset + rect.top - window.innerHeight + 200;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
  };

  /* ---------- Move matching grinder card to front of picker & auto-select ---------- */
  Promo420.prototype.moveMatchingGrinderToFront = function (pickerIdx, boxColor) {
    var grinderColor = COLOR_MAP[boxColor] || boxColor;
    var picker = this.pickers[pickerIdx];
    if (!picker) return;

    var container = picker.querySelector('.promo-420__picker-cards');
    if (!container) return;

    var cards = Array.from(container.querySelectorAll('[data-promo-card]'));
    var matchCard = null;

    cards.forEach(function (card) {
      if ((card.dataset.color || '').toLowerCase() === grinderColor) {
        matchCard = card;
      }
    });

    if (!matchCard) return;

    // Move to front if not already there
    if (matchCard !== cards[0]) {
      container.insertBefore(matchCard, cards[0]);
    }

    // Scroll container back to start so the matched card is visible
    container.scrollTo({ left: 0, behavior: 'smooth' });

    // Auto-select the matched card
    cards.forEach(function (c) {
      c.classList.remove('promo-420__card--selected');
    });
    matchCard.classList.add('promo-420__card--selected');

    this.selectedGrinders[pickerIdx] = {
      variantId: matchCard.dataset.variantId,
      color: matchCard.dataset.optionName,
      price: parseInt(matchCard.dataset.price, 10) || 0
    };

    var label = picker.querySelector('[data-picker-selected-name]');
    if (label) label.textContent = matchCard.dataset.optionName;

    this.updateUI();
  };

  /* ---------- BOGO detection ---------- */
  Promo420.prototype.detectBogoState = function () {
    var wasBogo = this.isBogo;
    this.isBogo = false;

    var bogoRadio = document.querySelector('#b1g1_checked');
    if (bogoRadio && bogoRadio.checked) this.isBogo = true;

    var bogoForm = document.querySelector('#b1g1_form');
    if (bogoForm && bogoForm.style.display !== 'none') this.isBogo = true;

    // Show/hide second picker
    if (this.pickers[1]) {
      if (this.isBogo) {
        this.pickers[1].classList.remove('promo-420__picker--hidden');
      } else {
        this.pickers[1].classList.add('promo-420__picker--hidden');
      }
    }

    if (wasBogo !== this.isBogo) {
      // When switching to BOGO, sync grinder colors for both boxes
      if (this.isBogo) {
        this.syncInitialBoxColors();
      }
      this.updateHeaderText();
      this.updateUI();
    }
  };

  Promo420.prototype.listenForTabChanges = function () {
    var self = this;
    var tabRadios = document.querySelectorAll('input[name="b1g1_selector"]');
    tabRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        // Delay slightly to let the accordion JS update #b1g1_form display first
        setTimeout(function () { self.detectBogoState(); }, 50);
      });
    });
    // Also listen for clicks on the tab labels (backup for missed change events)
    document.querySelectorAll('label.b1g1_selector').forEach(function (label) {
      label.addEventListener('click', function () {
        setTimeout(function () { self.detectBogoState(); }, 100);
      });
    });
  };

  /* ---------- Update header subtitle + pill for single/bogo ---------- */
  Promo420.prototype.updateHeaderText = function () {
    var count = this.isBogo ? this.bogoFreeCount : this.singleFreeCount;
    var giftTotal = this.grinderPrice * count;
    var plural = count > 1 ? 'Grinders' : 'Grinder';

    // Update green header subtitle
    if (this.freeCountEl) this.freeCountEl.textContent = count;
    if (this.giftValueEl) this.giftValueEl.textContent = formatMoney(giftTotal);

    if (this.headerSubtitleText) {
      this.headerSubtitleText.innerHTML =
        'Pick Your <strong data-promo-free-count>' + count + ' FREE ' + plural + '</strong>' +
        ' (<span data-promo-gift-value>' + formatMoney(giftTotal) + '</span> Gift)';
      this.freeCountEl = this.el.querySelector('[data-promo-free-count]');
      this.giftValueEl = this.el.querySelector('[data-promo-gift-value]');
    }

    // Update pill text — use original text from DOM, just swap singular/plural
    if (this.subtitleText && this.subtitleTextOriginal) {
      var updatedPill = this.subtitleTextOriginal
        .replace(/grinders/gi, plural.toLowerCase())
        .replace(/grinder/gi, plural.toLowerCase());
      this.subtitleText.textContent = updatedPill;
    }
  };

  /* ---------- Get active grinder selections ---------- */
  Promo420.prototype.getActiveGrinders = function () {
    var count = this.isBogo ? this.bogoFreeCount : this.singleFreeCount;
    var result = [];
    for (var i = 0; i < count; i++) {
      if (this.selectedGrinders[i]) {
        result.push(this.selectedGrinders[i]);
      }
    }
    return result;
  };

  /* ---------- Update order summary, ATC button & value display ---------- */
  Promo420.prototype.updateUI = function () {
    this.updateSummary();
    this.updateATCButton();
    this.updateValueDisplay();
    this.dispatchChangeEvent();
  };

  Promo420.prototype.updateSummary = function () {
    if (!this.summaryLines) return;

    var grinders = this.getActiveGrinders();
    var count = this.isBogo ? this.bogoFreeCount : this.singleFreeCount;
    var grinderTotal = this.grinderPrice * count;
    var plural = count > 1 ? 'Grinders' : 'Grinder';

    // Build grinder summary text
    var grinderColors = grinders.map(function (g) { return g.color; }).join(' + ');

    // Get box prices from ATC buttons
    var boxPrice = 0;
    var bogoDiscount = 0;
    var singleBoxPrice = 0;

    document.querySelectorAll('.hash-btn button').forEach(function (btn) {
      if (btn.dataset.finalPrice && !btn.dataset.bogoFinalPrice) {
        singleBoxPrice = parsePriceCents(btn.dataset.finalPrice);
      }
    });

    if (this.isBogo) {
      // Read the actual BOGO total from the visible button's displayed price
      var bogoForm = document.querySelector('#b1g1_form');
      if (bogoForm) {
        var visibleBtn = bogoForm.querySelector('.hash-btn button');
        if (visibleBtn) {
          var priceEl = visibleBtn.querySelector('.discounted-price .money') || visibleBtn.querySelector('.discounted-price');
          if (priceEl) {
            var bogoFinal = parsePriceCents(priceEl.textContent);
            // Get individual box prices from the two forms
            var topInput = document.querySelector('form#b1g1_top input[name="id"]');
            var regInput = document.querySelector('form#b1g1 input[name="id"]');
            var topPrice = topInput ? parseInt(topInput.dataset.price, 10) || 0 : 0;
            var regPrice = regInput ? parseInt(regInput.dataset.price, 10) || 0 : 0;
            boxPrice = topPrice + regPrice; // full price for both boxes (cents)
            bogoDiscount = boxPrice - bogoFinal;
          }
        }
      }
      if (boxPrice === 0) {
        boxPrice = singleBoxPrice * 2;
      }
    } else {
      boxPrice = singleBoxPrice;
    }

    var html = '';

    // Build box color labels
    var box1Name = capitalize(this.boxColors.box1 || '');
    var box2Name = capitalize(this.boxColors.box2 || '');

    // Box line
    if (this.isBogo) {
      var boxColorText = (box1Name || box2Name) ? ' <small>(' + (box1Name || '\u2014') + ' + ' + (box2Name || '\u2014') + ')</small>' : '';
      html += '<div class="promo-420__line">' +
        '<span class="promo-420__line-label">' + this.productName + ' \u00d7 2' + boxColorText + '</span>' +
        '<span class="promo-420__line-value">' +
          (bogoDiscount > 0 ? '<span style="text-decoration:line-through;color:#999;margin-right:6px;font-weight:400;font-size:13px">' + formatMoney(boxPrice) + '</span>' : '') +
          formatMoney(boxPrice - bogoDiscount) +
        '</span>' +
      '</div>';

      if (bogoDiscount > 0) {
        html += '<div class="promo-420__line promo-420__line--discount">' +
          '<span class="promo-420__line-label">' + this.bogoDiscountLabel + '</span>' +
          '<span class="promo-420__line-value">-' + formatMoney(bogoDiscount) + '</span>' +
        '</div>';
      }
    } else {
      var singleColorText = box1Name ? ' <small>(' + box1Name + ')</small>' : '';
      html += '<div class="promo-420__line">' +
        '<span class="promo-420__line-label">' + this.productName + singleColorText + '</span>' +
        '<span class="promo-420__line-value">' + formatMoney(boxPrice) + '</span>' +
      '</div>';
    }

    // Grinder line
    html += '<div class="promo-420__line promo-420__line--free">' +
      '<span class="promo-420__line-label">' + plural + ' \u00d7 ' + count +
        (grinderColors ? ' <small>(' + grinderColors + ')</small>' : '') +
      '</span>' +
      '<span class="promo-420__line-value">' +
        '<span class="promo-420__line-original">' + formatMoney(grinderTotal) + '</span>' +
        'FREE' +
      '</span>' +
    '</div>';

    this.summaryLines.innerHTML = html;

    // Savings
    var totalSavings = grinderTotal + bogoDiscount;
    if (this.savingsRow && totalSavings > 0) {
      this.savingsRow.style.display = 'flex';
      if (this.savingsValue) this.savingsValue.textContent = '-' + formatMoney(totalSavings);
    } else if (this.savingsRow) {
      this.savingsRow.style.display = 'none';
    }

    // Total
    var finalTotal = boxPrice - bogoDiscount; // grinders are free
    if (this.totalValue) {
      this.totalValue.textContent = formatMoney(finalTotal);
    }

    // Cache for value display
    this._lastBoxPrice = boxPrice;
    this._lastBogoDiscount = bogoDiscount;
    this._lastGrinderTotal = this.grinderPrice * count;
  };

  /* ---------- Strikethrough value display ---------- */
  Promo420.prototype.updateValueDisplay = function () {
    if (!this.valueDisplay) return;

    var count = this.isBogo ? this.bogoFreeCount : this.singleFreeCount;
    var grinderTotal = this.grinderPrice * count;
    var boxPrice = this._lastBoxPrice || 0;
    var bogoDiscount = this._lastBogoDiscount || 0;

    if (boxPrice <= 0) {
      this.valueDisplay.innerHTML = '';
      return;
    }

    var originalValue = boxPrice + grinderTotal;
    var finalPrice = boxPrice - bogoDiscount;

    if (originalValue > finalPrice) {
      this.valueDisplay.innerHTML =
        '<span class="promo-420__value-original">' + formatMoney(originalValue) + '</span>' +
        '<span class="promo-420__value-final">' + formatMoney(finalPrice) + '</span>';
    } else {
      this.valueDisplay.innerHTML = '';
    }
  };

  Promo420.prototype.updateATCButton = function () {
    var count = this.isBogo ? this.bogoFreeCount : this.singleFreeCount;
    var plural = count > 1 ? 'Grinders' : 'Grinder';

    // Update the ATC button text — append free grinder info
    document.querySelectorAll('.hash-btn button').forEach(function (btn) {
      var priceEl = btn.querySelector('.discounted-price');
      if (!priceEl) return;

      // Remove any existing promo suffix
      var existingPromo = btn.querySelector('.promo-420-atc-suffix');
      if (existingPromo) existingPromo.remove();

      var suffix = document.createElement('span');
      suffix.className = 'promo-420-atc-suffix';
      suffix.innerHTML = ' + <strong>' + count + ' FREE ' + plural + '</strong>';

      // Insert after the price element
      if (priceEl.nextSibling) {
        priceEl.parentNode.insertBefore(suffix, priceEl.nextSibling);
      } else {
        priceEl.parentNode.appendChild(suffix);
      }
    });
  };

  Promo420.prototype.dispatchChangeEvent = function () {
    var grinders = this.getActiveGrinders();
    this.el.dispatchEvent(new CustomEvent('promo420:change', {
      detail: { grinders: grinders, isBogo: this.isBogo },
      bubbles: true
    }));
  };

  /* ---------- Cart injection — unified batch after ATC ---------- */
  Promo420.prototype.interceptFormSubmission = function () {
    var self = this;

    // Pre-build payload on ATC click so it's ready instantly when variant:add fires
    var pendingAddonItems = null;

    // Listen for ATC form submit to pre-compute items
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('[is="product-form"], product-form form, form[action*="/cart/add"]')) return;

      // Pre-build grinder items immediately
      var grinders = self.getActiveGrinders().map(function (g) {
        return { id: parseInt(g.variantId, 10), quantity: 1, properties: { '_420_free_grinder': 'true' } };
      });

      // Pre-check gift eligibility against current cart
      var bucketGift = window._420BucketGift;
      var giftItem = null;
      if (bucketGift) {
        // Fetch current cart synchronously from cached data
        var currentTotal = bucketGift.cartTotal || 0;
        var mainProductPrice = self.basePriceCache['single'] || self.basePriceCache['bogo'] || 0;
        var projectedTotal = currentTotal + mainProductPrice;
        var giftAlreadyInCart = !!bucketGift.giftLineItem;

        if (projectedTotal >= bucketGift.THRESHOLD_CENTS && !giftAlreadyInCart) {
          giftItem = { id: bucketGift.selectedVariantId, quantity: 1, properties: { '_420_free_gift': 'true', upsell_origin: 'auto-gift' } };
          // Set flag so bucket fallback doesn't double-add
          bucketGift._batcherWillAdd = true;
        }
      }

      pendingAddonItems = [].concat(grinders);
      if (giftItem) pendingAddonItems.push(giftItem);
    }, true);

    // Fire the pre-built payload immediately when variant:add fires
    document.addEventListener('variant:add', function () {
      var items = pendingAddonItems;
      pendingAddonItems = null;

      // If no pre-built items, try building now as fallback
      if (!items || items.length === 0) {
        items = self.getActiveGrinders().map(function (g) {
          return { id: parseInt(g.variantId, 10), quantity: 1, properties: { '_420_free_grinder': 'true' } };
        });
      }

      if (!items || items.length === 0) return;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      }).then(function () {
        var bucketGift = window._420BucketGift;
        if (bucketGift) {
          bucketGift.isProcessing = false;
          bucketGift._batcherWillAdd = false;
        }
        // Single refresh
        if (window.upcartRefreshCart) window.upcartRefreshCart();
      });
    });
  };

  /* ---------- Helpers ---------- */
  function parsePriceCents(s) {
    if (!s) return 0;
    return Math.round(parseFloat(s.replace(/[^0-9.]/g, '')) * 100) || 0;
  }

  /* ---------- Mobile sticky ATC — custom bar with synced price ---------- */
  function initMobileStickyATC() {
    if (window.innerWidth >= 768) return;

    // Create custom sticky bar (hidden by default)
    var bar = document.createElement('div');
    bar.className = 'promo-420-sticky-bar promo-420-sticky-bar--hidden';
    var btn = document.createElement('button');
    btn.className = 'promo-420-sticky-bar__btn';
    btn.type = 'button';
    bar.appendChild(btn);
    document.body.appendChild(bar);

    function getActiveATCButton() {
      var bogoForm = document.querySelector('#b1g1_form');
      var isBogo = bogoForm && bogoForm.style.display !== 'none';
      var container = isBogo ? bogoForm : document.querySelector('#b1g1_not_form');
      if (!container) container = document.querySelector('.product-info__buy-buttons');
      if (!container) return null;
      return container.querySelector('.hash-btn button');
    }

    function syncPrice() {
      var realBtn = getActiveATCButton();
      if (!realBtn) return;

      // Copy styles from real button
      var styles = window.getComputedStyle(realBtn);
      btn.style.backgroundColor = styles.backgroundColor;
      btn.style.color = styles.color;

      // Build text from real button content
      var textEl = realBtn.querySelector('.has-btn-wrapper');
      if (textEl) {
        btn.textContent = textEl.textContent.replace(/\s+/g, ' ').trim();
      }
    }

    syncPrice();

    // Sync on tab change, variant change, promo change
    document.querySelectorAll('input[name="b1g1_selector"]').forEach(function (radio) {
      radio.addEventListener('change', function () { setTimeout(syncPrice, 150); });
    });
    document.addEventListener('variant:change', function () { setTimeout(syncPrice, 150); });

    // Re-sync periodically for dynamic price updates from theme JS
    setInterval(syncPrice, 1500);

    // Show/hide sticky bar based on real ATC button visibility
    var buyButtonsContainer = document.querySelector('.product-info__buy-buttons');
    if (buyButtonsContainer) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            bar.classList.add('promo-420-sticky-bar--hidden');
          } else {
            bar.classList.remove('promo-420-sticky-bar--hidden');
          }
        });
      }, { threshold: 0 });
      observer.observe(buyButtonsContainer);
    }

    // On tap, click the real ATC button
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var realBtn = getActiveATCButton();
      if (realBtn) realBtn.click();
    });
  }

  /* ---------- Init ---------- */
  function init() {
    document.querySelectorAll('[data-promo-420]').forEach(function (el) {
      el._promo420 = new Promo420(el);
    });
    initMobileStickyATC();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
